import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _uniqueId from 'lodash/uniqueId';
import { Icon, Typography } from "../../index";
import './dropdown.scss';

export const DropDown = ({
    autoFocus = false,
    colorStyle = 'neutral',
    disabled = false,
    error = false,
    hintMsg,
    id,
    label,
    maxLength,
    name,
    onChange = () => { },
    onBlur = () => { },
    options,
    required = false,
    value,
    ...inputProps
}) => {

    const dropdownRef = useRef();
    const [uId] = useState(_uniqueId('dropdown-'));
    const fieldId = id ? id : uId;
    const labelText = label ? `${label}${!required ? ' (Optional)' : ''}` : '';
    const _colorStyle = error ? "error" : colorStyle;

    let selectedOption = options && options.find(opt => opt.value == value)
    let indexFound = options.findIndex(opt => opt.value == value);
    const [selectedLabel, setSelectedLabel] = React.useState(selectedOption && selectedOption.label || '');  
    const [selectedIndex, setSelectedIndex] = React.useState(typeof indexFound === 'number' && indexFound !== -1 ? (required ? indexFound.toString() : (indexFound + 1).toString()) : '');
    const [showOptions, setShowOptions] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(value);

    useEffect(() => {
        setHasValueStyle();
    }, [value]);

    const handleSelect = (e, value, label, index) => {
        setHasValueStyle();
        setSelectedLabel(label);
        setSelectedIndex(index.toString());

        let event = {
            ...e, target: {
                value: value,
                name: dropdownRef.current.name ? dropdownRef.current.name : ""
            }
        }
        onChange(event);
        setShowOptions(false);

        let eventBlur = {
            ...event, type: 'blur'
        }

        onBlur(eventBlur);
    }

    const toggleOptions = (e) => {
        setShowOptions(!showOptions);
    }

    const handleBlur = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setShowOptions(false);

            let evt = {
                ...event, target: {
                    value: value,
                    name: dropdownRef.current.name ? dropdownRef.current.name : ""
                }
            }

            if (onBlur) {
                onBlur(evt);
            }
        }
    }

    // Event handler for keydowns
    const handleInputKeyDown = (e) => {
        switch (e.key) {
            case " ":
            case "SpaceBar":
            case "Enter":
                e.preventDefault();
                toggleOptions();
                break;
            default:
                break;
        }
    };

    // Event handler for keydowns
    const handleOptionsKeyDown = (e, value, label, index) => {
        switch (e.key) {
            case " ":
            case "SpaceBar":
            case "Enter":
                e.preventDefault();
                e.persist();
                handleSelect(e, value, label, index);
                break;
            case "Escape":
                e.preventDefault();
                setShowOptions(false);
            default:
                break;
        }
    };

    const setHasValueStyle = () => {
        if (dropdownRef.current.value && dropdownRef.current.value?.length > 0) {
            setHasValue(true);
        } else {
            setHasValue(false);
        }
    }

    const stateStyle = {
        error: {
            iconName: 'exclamation',
            iconColor: '$color-system-b1',
            msgColor: '$color-system-b1',
        },
        neutral: {
            iconName: '',
            iconColor: '',
            msgColor: '$color-secondary-a2',
        },
        success: {
            iconName: 'check',
            iconColor: '$color-system-a1',
            msgColor: '$color-system-a1',
        },
    };

    const dropdownClasses = [
        `bsw-dropdown`,
        `bsw-dropdown-color--${_colorStyle}`,
        `${disabled ? 'bsw-dropdown--disabled' : ''}`,
        `${hasValue ? 'bsw-dropdown--filled' : ''}`,
    ].join(' ');

    return (
        <div className={dropdownClasses} onBlur={handleBlur}>
            <div className={`bsw-dropdown__input`}>
                <input
                    {...inputProps}
                    autoFocus={autoFocus}
                    id={fieldId}
                    name={name}
                    type="text"
                    placeholder={labelText}
                    className="bsw-input"
                    disabled={disabled}
                    value={selectedLabel}
                    readOnly
                    ref={dropdownRef}
                    required={required}
                    role="combobox"
                    aria-owns={`${fieldId}-list`}
                    aria-haspopup="listbox"
                    aria-expanded={showOptions}
                    aria-activedescendant={selectedIndex ? `${fieldId}-option-${selectedIndex}` : ''}
                    onClick={toggleOptions}
                    onKeyDown={handleInputKeyDown}
                />
                {labelText && <label className="bsw-dropdown__label" htmlFor={fieldId}>
                    <Typography
                        type={(value || hasValue) ? "meta-01" : "body-02"}
                        color="$color-secondary-a2"
                        tag="div"
                    >
                        {labelText}
                    </Typography>
                </label>}
                <div className="bsw-dropdown__end">
                    {/* Below ICON need to be update depends on showoptions*/}
                    <div className="bsw-dropdown__arrow">
                        <Icon name={showOptions ? "chevron-up" : "chevron-down"} color="$color-primary-a2" size="small" />
                    </div>
                </div>
            </div>
            {(options && showOptions && !disabled) &&
                <ul
                    id={`${fieldId}-list`}
                    className="bsw-dropdown__options"
                    role="listbox"
                    aria-activedescendant={selectedIndex ? `${fieldId}-option-${selectedIndex}` : ''}
                    tabIndex="-1"
                >
                    {!required && 
                        <li
                            id={`${fieldId}-option-0`}
                            role="option"
                            tabIndex="0"
                            className={`bsw-dropdown__option-item`}
                            onClick={(e) => handleSelect(e, '', '', '')}
                            onKeyDown={(e) => handleOptionsKeyDown(e, '', '', '')}
                        >
                            <Typography type="body-02" color="$color-secondary-a2">Please select</Typography>
                        </li>
                    }
                {
                    options.map((opt, index) => {
                        const optionIndex = required ? index.toString() : (index + 1).toString();
                        return (
                            <li
                                id={`${fieldId}-option-${optionIndex}`}
                                role="option"
                                aria-selected={optionIndex === selectedIndex}
                                tabIndex="0"
                                className={`bsw-dropdown__option-item ${optionIndex === selectedIndex ? 'bsw-dropdown__option-item--active' : ''}`}
                                key={index}
                                onClick={(e) => handleSelect(e, opt.value, opt.label, optionIndex)}
                                onKeyDown={(e) => handleOptionsKeyDown(e, opt.value, opt.label, optionIndex) }
                            >
                                <Typography type="body-02" color="$color-secondary-a2">{opt.label}</Typography>
                            </li>
                        )
                    })
                }
             </ul>}
            {hintMsg &&
                <div className="bsw-dropdown__hint">
                    {(_colorStyle !== 'neutral') &&
                        <div className="bsw-dropdown__validation-icon">
                            <Icon
                                name={stateStyle[_colorStyle]['iconName']}
                                color={stateStyle[_colorStyle]['iconColor']}
                                size="small"
                            />
                        </div>
                    }
                    <Typography type="meta-01" color={stateStyle[_colorStyle]['msgColor']}>
                        {hintMsg}
                    </Typography>
                </div>
            }
        </div>
    )
}

DropDown.propTypes = {
    disabled: PropTypes.bool,
    label: PropTypes.string,
    value: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    required: PropTypes.bool,
    errorMsg: PropTypes.string,
    options: PropTypes.array
}